import React, { useEffect, useState } from "react";
import { useForm, Controller, FormProvider, useWatch } from "react-hook-form";
import {
  Box,
  Button,
  Checkbox,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  Modal,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useGetDdlRegionListQuery } from "store/services/LocationsApi/regionApi";
import BaseAutocomplete from "components/Shared/Base/BaseAutocomplete";
import { Edit } from "@mui/icons-material";
import LoadingWrapper from "components/Shared/LoadingWrapper";
import { CustomCountryPhoneInput } from "components/Shared/CustomCountryPhoneInput";
import { useLazyGetDistrictsByRegionIdQuery } from "store/services/LocationsApi/districtApi";
import { useLazyGetMunicipalityByDistrictIdQuery } from "store/services/LocationsApi/municipalityApi";
import { useLazyGetBranchListQuery } from "store/services/branchApi";
import BaseModal from "components/Shared/BaseModel/BaseModel";
import { useLazyGetDDLWarehouseListQuery } from "store/services/warehouseApi";
import { useAuth } from "hooks";

const BranchForm = ({
  branchDetail = null,
  isBusy = false,
  onSubmit = () => {},
}) => {
  const navigate = useNavigate();
  const { viewEntity } = useAuth();

  const formHookMethods = useForm({
    defaultValues: {
      code: branchDetail?.code || "",
      name: branchDetail?.name || "",
      coordinates: branchDetail?.coordinates || { lat: "", long: "" },
      address: branchDetail?.address || "",
      phone:
        branchDetail?.phone ||
        `${viewEntity?.entity?.country?.idd}--${viewEntity?.entity?.country?.cca2}`,
      areasCovered: branchDetail?.areasCovered || "",
      workingHours: branchDetail?.workingHours || [],
      region: branchDetail?.region || "",
      district: branchDetail?.district || "",
      municipality: branchDetail?.municipality || "",
      status: branchDetail?.status || "Active",
      canAddress: branchDetail?.canAddress || "",
      branchManager: branchDetail?.branchManager || "",
      parentBranch: branchDetail?.parentBranch || null,
      hasWarehouse: !!branchDetail?.warehouse,
      warehouse: branchDetail?.warehouse || null,
    },
  });

  const {
    control,
    handleSubmit,
    setValue,
    reset,
    formState: { errors },
  } = formHookMethods;

  const [isAddAddressModalActive, setIsAddAddressModalActive] = useState(false);
  const _watchRegion = useWatch({ control, name: "region" });
  const _watchDistrict = useWatch({ control, name: "district" });
  const _watchHasWarehouse = useWatch({ control, name: "hasWarehouse" });

  const { data: regionRes, isLoading: isLoadingRegions } =
    useGetDdlRegionListQuery();
  const [fetchBranches, { data: branchRes, isLoading: isLoadingBranches }] =
    useLazyGetBranchListQuery();
  const [
    fetchDistricts,
    { data: districtRes = [], isLoading: loadingDistricts },
  ] = useLazyGetDistrictsByRegionIdQuery();
  const [
    fetchMunicipality,
    { data: municipalityRes = [], isLoading: loadingMunicipality },
  ] = useLazyGetMunicipalityByDistrictIdQuery();
  const [fetchWarehouses, { data: warehouses, isLoading: loadingWarehouses }] =
    useLazyGetDDLWarehouseListQuery();

  useEffect(() => {
    fetchBranches({ status: "Active" });
    if (viewEntity?.entity?._id && viewEntity?.entity?.account?._id) {
      fetchWarehouses({
        account: viewEntity.entity.account._id,
        entity: viewEntity.entity._id,
      });
    }
  }, [fetchBranches, fetchWarehouses, viewEntity]);

  useEffect(() => {
    if (_watchRegion?._id) {
      fetchDistricts(_watchRegion._id);
      if (branchDetail?.region?._id !== _watchRegion._id) {
        setValue("district", "");
      }
    } else {
      setValue("district", "");
    }
  }, [_watchRegion, fetchDistricts, setValue, branchDetail]);

  useEffect(() => {
    if (_watchDistrict?._id) {
      fetchMunicipality(_watchDistrict._id);
      if (branchDetail?.district?._id !== _watchDistrict._id) {
        setValue("municipality", "");
      }
    } else {
      setValue("municipality", "");
    }
  }, [_watchDistrict, fetchMunicipality, setValue, branchDetail]);

  useEffect(() => {
    if (branchDetail) {
      reset({
        ...branchDetail,
        coordinates: branchDetail.coordinates || { lat: "", long: "" },
        phone:
          branchDetail.phone ||
          `${viewEntity?.entity?.country?.idd}--${viewEntity?.entity?.country?.cca2}`,
        workingHours: branchDetail.workingHours || [],
        type: branchDetail?.type?._id || "",
        branchManager: branchDetail?.branchManager
          ? {
              label: branchDetail.branchManager.name,
              id: branchDetail.branchManager._id,
            }
          : "",
        parentBranch: branchDetail.parentBranch || null,
        hasWarehouse: !!branchDetail?.warehouse,
        warehouse: branchDetail?.warehouse || null,
      });
    }
  }, [
    branchDetail,
    reset,
    viewEntity?.entity?.country?.idd,
    viewEntity?.entity?.country?.cca2,
  ]);

  const handleBranchFormSubmit = (payload) => {
    const formattedPayload = {
      ...payload,
      region: branchDetail
        ? { oldId: branchDetail?.region?._id, newId: payload?.region?._id }
        : payload.region?._id,
      district: branchDetail
        ? { oldId: branchDetail?.district?._id, newId: payload?.district?._id }
        : payload.district?._id,
      municipality: branchDetail
        ? {
            oldId: branchDetail?.municipality?._id,
            newId: payload?.municipality?._id,
          }
        : payload.municipality?._id,
      warehouse: _watchHasWarehouse ? payload.warehouse : null,
    };
    delete formattedPayload.hasWarehouse;
    onSubmit(formattedPayload);
  };

  const renderTextField = (
    name,
    label,
    type = "text",
    rules = {},
    required = false
  ) => (
    <Grid item md={6}>
      <Controller
        name={name}
        control={control}
        rules={rules}
        render={({ field, fieldState: { error } }) => (
          <TextField
            fullWidth
            label={`${label}${required ? "" : ""}`}
            type={type}
            size="small"
            error={!!error}
            helperText={error?.message}
            {...field}
          />
        )}
      />
    </Grid>
  );

  return (
    <LoadingWrapper>
      <FormProvider {...formHookMethods}>
        <Box padding={3}>
          <Grid container spacing={3}>
            {renderTextField(
              "name",
              "Name*",
              "text",
              { required: "Name is required" },
              true
            )}
            <Grid item md={6}>
              <Controller
                name="code"
                control={control}
                rules={{
                  required: "Code is required",
                  validate: (value) =>
                    value.length > 8
                      ? "Code must be at most 8 characters"
                      : true,
                }}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    fullWidth
                    required
                    label="Code"
                    type="text"
                    size="small"
                    error={!!error}
                    helperText={error?.message}
                    inputProps={{
                      maxLength: 8,
                      style: { textTransform: "uppercase" },
                    }}
                    onChange={(e) => {
                      field.onChange(e.target.value.toUpperCase());
                    }}
                    value={field.value}
                  />
                )}
              />
            </Grid>

            {renderTextField("coordinates.lat", "Latitude", "number")}
            {renderTextField("coordinates.long", "Longitude", "number")}
            {renderTextField(
              "address",
              "Address*",
              "text",
              { required: "Address is required" },
              true
            )}
            {renderTextField(
              "areasCovered",
              "Areas Covered*",
              "text",
              { required: "Areas Covered is required" },
              true
            )}

            <Grid item md={6}>
              <Controller
                name="phone"
                control={control}
                rules={{
                  required: "Phone is required",

                  validate: (value) => {
                    const digits = value.replace(/\D/g, "");
                    if (digits.length < 7 || digits.length > 17) {
                      return "Phone number must be between 9 and 16 digits";
                    }
                    return true;
                  },
                }}
                render={({ field, fieldState: { error } }) => (
                  <CustomCountryPhoneInput
                    placeholder="Phone Number"
                    label="Phone*"
                    {...field}
                    error={!!error}
                    helperText={error?.message}
                  />
                )}
              />
            </Grid>

            <Grid item md={6}>
              <Controller
                name="region"
                control={control}
                rules={{ required: "Please select a Region" }}
                render={({ field }) => (
                  <BaseAutocomplete
                    fullWidth
                    required
                    label="Region"
                    loading={isLoadingRegions}
                    loadingText="Loading Regions..."
                    options={regionRes || []}
                    getOptionLabel={(opt) => opt?.name || ""}
                    isOptionEqualToValue={(option, value) =>
                      option?._id === value?._id
                    }
                    value={field.value || null}
                    onChange={(_, data) => setValue("region", data)}
                    error={!!errors?.region}
                    helperText={errors?.region?.message}
                  />
                )}
              />
            </Grid>

            <Grid item md={6}>
              <Controller
                name="district"
                control={control}
                rules={{
                  required: _watchRegion?._id
                    ? "Please select a District"
                    : false,
                }}
                render={({ field }) => (
                  <BaseAutocomplete
                    fullWidth
                    required={!!_watchRegion?._id}
                    disabled={!_watchRegion?._id}
                    label={`District${_watchRegion?._id ? "" : ""}`}
                    options={districtRes?.data || []}
                    loading={loadingDistricts}
                    loadingText="Loading Districts..."
                    getOptionLabel={(opt) => opt?.name || ""}
                    isOptionEqualToValue={(option, value) =>
                      option?._id === value?._id
                    }
                    value={field.value || null}
                    onChange={(_, data) => setValue("district", data)}
                    noOptionsText={
                      _watchRegion?._id
                        ? "No districts found"
                        : "Please select a region first"
                    }
                    error={!!errors?.district}
                    helperText={errors?.district?.message}
                  />
                )}
              />
            </Grid>

            <Grid item md={6}>
              <Controller
                name="municipality"
                control={control}
                rules={{
                  required: _watchDistrict?._id
                    ? "Please select a municipality"
                    : false,
                }}
                render={({ field }) => (
                  <BaseAutocomplete
                    fullWidth
                    required={!!_watchDistrict?._id}
                    disabled={!_watchDistrict?._id}
                    label={`Municipality${_watchDistrict?._id ? "" : ""}`}
                    options={municipalityRes?.data || []}
                    loading={loadingMunicipality}
                    loadingText="Loading municipality..."
                    getOptionLabel={(opt) => opt?.name || ""}
                    isOptionEqualToValue={(option, value) =>
                      option?._id === value?._id
                    }
                    value={field.value || null}
                    onChange={(_, data) => setValue("municipality", data)}
                    noOptionsText={
                      _watchDistrict?._id
                        ? "No municipality found"
                        : "Please select a district first"
                    }
                    error={!!errors?.municipality}
                    helperText={errors?.municipality?.message}
                  />
                )}
              />
            </Grid>

            <Grid item md={6}>
              <Controller
                name="parentBranch"
                control={control}
                render={({ field }) => (
                  <BaseAutocomplete
                    fullWidth
                    size="small"
                    label="Parent Branch"
                    value={field.value || null}
                    options={branchRes?.data || []}
                    onChange={(_, data) => field.onChange(data)}
                    loading={isLoadingBranches}
                    loadingText="Loading Branches..."
                    limitTags={2}
                    getOptionLabel={(option) => option?.name || ""}
                    disabled={!!branchDetail}
                    isOptionEqualToValue={(option, value) =>
                      option?._id === value?._id
                    }
                    error={!!errors?.parentBranch}
                    helperText={errors?.parentBranch?.message}
                  />
                )}
              />
            </Grid>

            <Grid item md={6}>
              <Controller
                name="hasWarehouse"
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    control={
                      <Switch
                        color="primary"
                        checked={field.value}
                        onChange={(e) => {
                          field.onChange(e.target.checked);
                          if (!e.target.checked) {
                            setValue("warehouse", null);
                          }
                        }}
                      />
                    }
                    label="Has Warehouse?"
                  />
                )}
              />
            </Grid>

            {_watchHasWarehouse && (
              <Grid item md={6}>
                <Controller
                  name="warehouse"
                  control={control}
                  render={({ field }) => (
                    <BaseAutocomplete
                      fullWidth
                      label="Warehouse"
                      multiple={false}
                      value={field.value || null}
                      options={warehouses || []}
                      onChange={(_, data) => field.onChange(data)}
                      getOptionLabel={(option) =>
                        option ? `${option.name} - ${option.warehouse_id}` : ""
                      }
                      isOptionEqualToValue={(option, value) =>
                        option?._id === value?._id
                      }
                      loading={loadingWarehouses}
                      loadingText="Loading Warehouses..."
                      error={!!errors?.warehouse}
                      helperText={errors?.warehouse?.message || "Optional"}
                    />
                  )}
                />
              </Grid>
            )}

            <Grid item xs={12}>
              <Box className="mb-3">Working Hours</Box>
              <Items
                control={control}
                setValue={setValue}
                workingTimes={branchDetail?.workingHours}
              />
            </Grid>

            {branchDetail && (<Grid item md={6}>
              <Controller
                name="status"
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    control={
                      <Switch
                        color="primary"
                        checked={field.value === "Active"}
                        onChange={(e) =>
                          field.onChange(
                            e.target.checked ? "Active" : "Inactive"
                          )
                        }
                      />
                    }
                    label="Is Active?"
                  />
                )}
              />
            </Grid>)}
          </Grid>
        </Box>
        <Divider />
        <Box display="flex" justifyContent="flex-end" paddingX={3} paddingY={2}>
          <Button disabled={isBusy} onClick={() => navigate(-1)}>
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={isBusy}
            onClick={handleSubmit(handleBranchFormSubmit)}
          >
            {branchDetail ? "Update" : "Add"}
          </Button>
        </Box>
        <BaseModal
          isOpen={isAddAddressModalActive}
          onClose={() => setIsAddAddressModalActive(false)}
          title="Add CAN Address"
          width="1200px"
          top="52%"
          left="58%"
        ></BaseModal>
      </FormProvider>
    </LoadingWrapper>
  );
};

const Items = ({ control, setValue, workingTimes }) => {
  const [open, setOpen] = useState(false);
  const [selectedDay, setSelectedDay] = useState(null);
  const [workingHours, setWorkingHours] = useState([
    { day: "Monday", startsAt: "10:00", closesAt: "18:00", isClosed: false },
    { day: "Tuesday", startsAt: "10:00", closesAt: "18:00", isClosed: false },
    { day: "Wednesday", startsAt: "10:00", closesAt: "18:00", isClosed: false },
    { day: "Thursday", startsAt: "10:00", closesAt: "18:00", isClosed: false },
    { day: "Friday", startsAt: "10:00", closesAt: "18:00", isClosed: false },
    { day: "Saturday", startsAt: "10:00", closesAt: "18:00", isClosed: false },
    { day: "Sunday", startsAt: "10:00", closesAt: "18:00", isClosed: false },
  ]);

  const handleEdit = (day) => {
    setSelectedDay(day);
    setOpen(true);
  };

  const handleSave = () => {
    const updatedHours = workingHours.map((item) =>
      item.day === selectedDay
        ? {
            ...item,
            startsAt: document.getElementById("start").value || "10:00",
            closesAt: document.getElementById("end").value || "18:00",
            isClosed: false,
          }
        : item
    );
    setWorkingHours(updatedHours);
    setValue("workingHours", updatedHours);
    setOpen(false);
  };

  const handleClosedChange = (day) => {
    const updatedHours = workingHours.map((item) =>
      item.day === day
        ? {
            ...item,
            isClosed: !item.isClosed,
            startsAt: !item.isClosed ? null : "10:00",
            closesAt: !item.isClosed ? null : "18:00",
          }
        : item
    );
    setWorkingHours(updatedHours);
    setValue("workingHours", updatedHours);
  };

  useEffect(() => {
    if (workingTimes) {
      const data = workingTimes.map((item) => ({
        ...item,
        isClosed: item?.closesAt === null,
      }));
      setWorkingHours(data);
    }
  }, [workingTimes]);

  return (
    <Box>
      {workingHours.map(({ day, startsAt, closesAt, isClosed }, index) => (
        <Box
          key={index}
          display="flex"
          justifyContent="space-between"
          width="100%"
        >
          <Typography variant="body1" mt={2}>
            {day}
          </Typography>
          <Box display="flex">
            <FormControlLabel
              control={
                <Checkbox
                  checked={isClosed}
                  onChange={() => handleClosedChange(day)}
                  color="primary"
                />
              }
              label="Closed"
            />
            {!isClosed && (
              <Box display="flex" alignItems="center">
                <Typography variant="body1" mt={1}>
                  {startsAt || "10:00"} - {closesAt || "18:00"}
                </Typography>
                <IconButton onClick={() => handleEdit(day)}>
                  <Edit />
                </IconButton>
              </Box>
            )}
          </Box>
        </Box>
      ))}
      <Modal open={open} onClose={() => setOpen(false)}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 500,
            bgcolor: "background.paper",
            borderRadius: 1,
            boxShadow: 24,
            p: 4,
          }}
        >
          <Typography variant="h6" align="center" mb={4}>
            Edit Working Hours
          </Typography>
          <Box display="flex" justifyContent="space-around" gap={4}>
            <Box display="flex" flexDirection="column">
              <label htmlFor="start" className="text-sm font-medium">
                Start Time
              </label>
              <input
                id="start"
                type="time"
                defaultValue={
                  workingHours.find((item) => item.day === selectedDay)
                    ?.startsAt || "10:00"
                }
                className="mt-2 rounded-md border border-gray-300 p-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </Box>
            <Box display="flex" flexDirection="column">
              <label htmlFor="end" className="text-sm font-medium">
                End Time
              </label>
              <input
                id="end"
                type="time"
                defaultValue={
                  workingHours.find((item) => item.day === selectedDay)
                    ?.closesAt || "18:00"
                }
                className="mt-2 rounded-md border border-gray-300 p-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </Box>
          </Box>
          <Box display="flex" justifyContent="flex-end" gap={2} mt={4}>
            <Button
              onClick={() => setOpen(false)}
              sx={{ bgcolor: "grey.300", color: "black" }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              variant="contained"
              sx={{ bgcolor: "blue.500" }}
            >
              Save
            </Button>
          </Box>
        </Box>
      </Modal>
    </Box>
  );
};

export default BranchForm;
